import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { nanoid } from 'nanoid'

import { SiteState } from '../store/site-reducer'
import { GraphQLState } from '../store/graphql-reducer'
import styles from '../component-styles/settings-v2.module.scss'
import ChangeInput, { CopyButton } from './change-input'
import BillingPlanCard from './billing-plan-card'
import FeaturesSettingsCard from './features-settings-card'
import { removeAllAccounts } from '../api-client/settings-client'
import {
  updateAccountDescriptionAsync,
  getUserAccountDetailsGQL,
  updateQrSettingsGQL,
  setDashboardSettingsGQL,
  getCampaignLinkDashboardMetaGQL,
  logActionAsync,
} from '../store/actions'
import Modal, { ModalHead, ModalBody } from './modal'
import { deleteWord, matchTypesDropDown } from '../core/variables'
import { logout } from '../api-client/user-client'

import Heading from './heading'
import Input, { Label } from './input'
import Button from './button'
import Tooltip from './tooltip'
import {
  makeHexString,
  getDefaultOption,
  getItemByKeyValue,
  sortData,
} from '../helpers'
import { SelectBoxSimple } from './select-box'
import { ImgUploader } from './img-uploader'
import {
  deleteQrLogo,
  uploadAccountLogo,
  uploadQrLogo,
} from '../api-client/graphql-client'

interface Props {
  isEnterpriseAccount: boolean
  className?: string
}

export default function BillingAndAccountSettings({
  isEnterpriseAccount,
  className,
}: Props) {
  const showShape = false
  const showTransparency = false
  const showBilling = false

  const dispatch = useDispatch()
  const defaultQrStyle = 'squares'
  const [qrStyle, setQrStyle] = useState(defaultQrStyle)
  const [performance, setPerfomance] = useState('full')
  const [active, setActive] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const {
    site: { selected },
    graphql,
  }: { site: SiteState; graphql: GraphQLState } = useSelector(
    (s: any): any => s,
  )

  const { currentAccount, currentUser, campaignLinkDashboardMeta } = graphql

  const isAdminUser =
    currentUser &&
    currentUser.currentAccountPermission &&
    (currentUser.currentAccountPermission === 'admin' ||
      currentUser.currentAccountPermission === 'support')

  useEffect(() => {
    if (!currentAccount) {
      dispatch(getUserAccountDetailsGQL())
    } else {
      setQrStyle(
        getDefaultOption(
          currentAccount,
          'qrSettings',
          'qrStyle',
          defaultQrStyle,
        ),
      )
    }
  }, [currentAccount])

  const { fgColour, bgColour, qrTransparentLogo } = useMemo(() => {
    return {
      fgColour: getDefaultOption(
        currentAccount,
        'qrSettings',
        'fgColour',
        '000000',
      ).replace('#', ''),
      qrTransparentLogo: getDefaultOption(
        currentAccount,
        'qrSettings',
        'transparentLogo',
        'false',
      ),
      bgColour: getDefaultOption(
        currentAccount,
        'qrSettings',
        'bgColour',
        'FFFFFF',
      ).replace('#', ''),
    }
  }, [currentAccount?.qrSettings])

  const qrCodeLogoList = useMemo(() => {
    if (
      currentAccount &&
      currentAccount.qrCodeLogoList &&
      Array.isArray(currentAccount.qrCodeLogoList)
    ) {
      return currentAccount.qrCodeLogoList
    }
    return []
  }, [currentAccount?.qrCodeLogoList])

  const accountID = selected?.accountID || ''
  const companyID = selected?.companyID || ''
  const accountName = selected?.accountName || ''
  const homepage = selected?.homepage || ''
  const uid = currentUser?.userID || ''
  const logoLink = `${currentAccount?.logoLink || ''}`.replace(
    '/static/logos/PNG/uplogo.png',
    '',
  )

  useEffect(() => {
    const mT = getDefaultOption(
      campaignLinkDashboardMeta,
      'onloadDefaultOptions',
      'matchType',
      'full',
    )
    if (mT !== 'full') {
      const res = getItemByKeyValue(matchTypesDropDown, 'value', mT)
      if (res !== -1) {
        setPerfomance(res.value)
        return
      }
    }
    setPerfomance(mT)
  }, [campaignLinkDashboardMeta])

  const { availableDimensions } = campaignLinkDashboardMeta || {
    availableDimensions: [],
  }

  useEffect(() => {
    dispatch(getCampaignLinkDashboardMetaGQL())
  }, [])

  const matchTypeMerged = useMemo(() => {
    if (!availableDimensions) {
      return [...matchTypesDropDown]
    }
    return [
      ...matchTypesDropDown,
      ...sortData(
        availableDimensions.map((item) => {
          const { dimensionName, helpText } = item
          return {
            name: dimensionName,
            shortName: dimensionName,
            value: dimensionName,
            tooltip: helpText,
          }
        }),
        'name',
        true,
        null,
        true,
      ),
    ]
  }, [availableDimensions])

  return (
    <div className={className}>
      <h3>Details</h3>
      <div className={styles.subsection}>
        <div className={styles.fieldRow}>
          <ChangeInput
            onClick={async (nextAccountName) => {
              const response = nextAccountName !== ''
              if (!response || accountID === '') return 'Not valid'
              const res = dispatch(
                updateAccountDescriptionAsync({
                  accountID,
                  accountName: nextAccountName,
                }),
              )
              if (res) {
                // callUpdate()
                return 'Updated'
              }
              return 'Failed'
            }}
            tooltip="This is the name of your account."
            label="Account name"
            type="text"
            name="accountName"
            initialValue={accountName || ''}
          />
          <ChangeInput
            tooltip="This is your homepage (use full domain name, e.q. https://www.mezzolabs.com)."
            onClick={async (nextHomepage) => {
              const response = nextHomepage !== ''
              if (!response || accountID === '') return 'Not valid'
              const res = dispatch(
                updateAccountDescriptionAsync({
                  accountID,
                  websiteURL: nextHomepage,
                }),
              )
              if (res) {
                // callUpdate()
                return 'Updated'
              }
              return 'Failed'
            }}
            label="Homepage"
            type="text"
            name="homepage"
            initialValue={homepage || ''}
          />
        </div>
        <div className={styles.fieldRow}>
          <div>
            <Label id="logo">
              Company logo
              <Tooltip
                className={styles.tooltip}
                message="This logo will be displayed on the top left of Uplifter reports. We support png, gif or jpeg files up to 1MB."
              />
            </Label>
            <ImgUploader
              logoLink={logoLink}
              imgType="logo"
              uploadFn={async (file: File) => {
                const res = await uploadAccountLogo({ file })
                if (res === true) {
                  dispatch(getUserAccountDetailsGQL())
                  const actionData = {
                    action: 'company-logo-changed',
                    longDescription: '',
                    websiteSection: 'settings',
                  }
                  dispatch(logActionAsync(actionData))
                  return true
                }
                return false
              }}
            />
          </div>
        </div>
      </div>

      <span className={styles.spacer} />

      {showBilling && (
        <>
          <h3>Your plan</h3>
          <div className={styles.subsection}>
            <p className={styles.byline}>
              The plan you are currently subscribed to.
            </p>
            <BillingPlanCard />
          </div>
          <span className={styles.spacer} />
        </>
      )}

      <h3>Features</h3>
      <p className={styles.byline}>
        Change which features your users can access.
      </p>
      <div className={styles.subsection}>
        <FeaturesSettingsCard />
      </div>

      <span className={styles.spacer} />

      <h3>Report preferences</h3>
      <p className={styles.byline}>Set your default performance report view.</p>
      <div className={styles.subsection} style={{ marginBottom: 16 }}>
        <SelectBoxSimple
          disabled={!isAdminUser}
          key={performance}
          name="Performance"
          value={performance}
          onChange={(performanceType) => {
            setPerfomance(performanceType)
            dispatch(
              setDashboardSettingsGQL({
                onloadDefaultOptions: [
                  {
                    name: 'matchType',
                    enabled: false,
                    value: performanceType,
                  },
                ],
              }),
            )
          }}
        >
          {matchTypeMerged.map((item) => {
            return (
              <option value={item.value} key={item.value}>
                {item.name}
              </option>
            )
          })}
        </SelectBoxSimple>
      </div>

      <span className={styles.spacer} />

      <h3>QR code</h3>
      <Label id="qrLogo" className={styles.byline}>
        Logo used in centre of QR codes.
        <Tooltip message="Simple versions of your logo work best, ideal size 300px x 300px &amp; less than 1MB." />
      </Label>
      <div className={styles.subsection}>
        {qrCodeLogoList.length > 0 &&
          qrCodeLogoList.map((logoLinkQR) => {
            return (
              <ImgUploader
                key={logoLinkQR}
                logoLink={logoLinkQR}
                imgType="qrLogo"
                uploadFn={async (file: File) => {
                  const res = await uploadQrLogo({ file })
                  if (res === true) {
                    dispatch(getUserAccountDetailsGQL())
                    const actionData = {
                      action: 'company-qr-logo-changed',
                      longDescription: '',
                      websiteSection: 'settings',
                    }
                    dispatch(logActionAsync(actionData))
                    return true
                  }
                  return false
                }}
                deleteFn={
                  qrCodeLogoList.length > 1
                    ? async () => {
                        await deleteQrLogo(logoLinkQR)
                        dispatch(getUserAccountDetailsGQL())
                        const actionData = {
                          action: 'company-qr-logo-deleted',
                          longDescription: '',
                          websiteSection: 'settings',
                        }
                        dispatch(logActionAsync(actionData))
                      }
                    : undefined
                }
              />
            )
          })}
        {qrCodeLogoList.length === 0 && (
          <ImgUploader
            logoLink=""
            imgType="qrLogo"
            uploadFn={async (file: File) => {
              const res = await uploadQrLogo({ file })
              if (res === true) {
                dispatch(getUserAccountDetailsGQL())
                const actionData = {
                  action: 'company-qr-logo-changed',
                  longDescription: '',
                  websiteSection: 'settings',
                }
                dispatch(logActionAsync(actionData))
                return true
              }
              return false
            }}
          />
        )}
        {showTransparency && (
          <>
            <Label modalHeading id="backgroundBehind" className={styles.label}>
              Background behind logo
            </Label>
            <Input
              name="type"
              id="backgroundBehind"
              label="Transparent"
              type="checkbox"
              checked={qrTransparentLogo === 'true'}
              onChange={(e): any => {
                e.preventDefault()
                setQrStyle('dots')
                dispatch(
                  updateQrSettingsGQL({
                    qrSettings: [
                      {
                        name: 'transparentLogo',
                        enabled: true,
                        value: e.target.checked ? 'true' : 'false',
                      },
                    ],
                  }),
                )
              }}
            />
          </>
        )}
        <div className={styles.fieldRow}>
          <ChangeInput
            label="Default foreground colour"
            tooltip="Use RGB hex value, e.q. #000000 for black."
            type="text"
            name="fgColour"
            initialValue={fgColour}
            prefix="#"
            onBeforeChange={(val) => {
              return makeHexString(val)
            }}
            onClick={(newValue) => {
              if (newValue !== '') {
                dispatch(
                  updateQrSettingsGQL({
                    qrSettings: [
                      {
                        name: 'fgColour',
                        enabled: true,
                        value: `#${newValue}`,
                      },
                    ],
                  }),
                )
              }
            }}
          />
          <ChangeInput
            label="Default background colour"
            tooltip="Use RGB hex value, e.q. #FFFFFF for white."
            type="text"
            name="bgColour"
            initialValue={bgColour}
            prefix="#"
            onBeforeChange={(val) => {
              return makeHexString(val)
            }}
            onClick={(newValue) => {
              if (newValue !== '') {
                dispatch(
                  updateQrSettingsGQL({
                    qrSettings: [
                      {
                        name: 'bgColour',
                        enabled: true,
                        value: `#${newValue}`,
                      },
                    ],
                  }),
                )
              }
            }}
          />
        </div>
        {showShape && (
          <div className={styles.radioRow}>
            <Label modalHeading id="type" className={styles.label}>
              Shape of QR modules
            </Label>
            <div className={styles.alignRow}>
              <Input
                name="type"
                id={nanoid()}
                label="Squares"
                type="radio"
                value="squares"
                checked={qrStyle !== 'dots'}
                onClick={(e): any => {
                  e.preventDefault()
                  setQrStyle('squares')
                  dispatch(
                    updateQrSettingsGQL({
                      qrSettings: [
                        {
                          name: 'qrStyle',
                          enabled: true,
                          value: 'squares',
                        },
                      ],
                    }),
                  )
                }}
              />
              <Input
                name="type"
                id={nanoid()}
                label="Dots"
                type="radio"
                value="dots"
                checked={qrStyle === 'dots'}
                onClick={(e): any => {
                  e.preventDefault()
                  setQrStyle('dots')
                  dispatch(
                    updateQrSettingsGQL({
                      qrSettings: [
                        {
                          name: 'qrStyle',
                          enabled: true,
                          value: 'dots',
                        },
                      ],
                    }),
                  )
                }}
              />
            </div>
          </div>
        )}
      </div>

      <span className={styles.spacer} />

      <div className={styles.subsection}>
        <h3 className={styles.hiddenButton}>
          <span>Uplifter Support Information</span>
          <CopyButton
            value={`uid: ${uid}
accountID: ${accountID}
companyID: ${companyID}`}
          >
            Copy all
          </CopyButton>
        </h3>
        <p className={styles.byline}>
          Send these identifiers to the Uplifter team when you need account
          support.
        </p>
        <ChangeInput
          label="User ID"
          type="text"
          name="UserID"
          initialValue={uid}
          readOnly
          copyButton
          copyText={`uid: ${uid}`}
        />
        <ChangeInput
          label="Account ID"
          type="text"
          name="AccountID"
          initialValue={accountID}
          readOnly
          copyButton
          copyText={`accountID: ${accountID}`}
        />
        <ChangeInput
          label="Company ID"
          type="text"
          name="CompanyID"
          initialValue={companyID}
          readOnly
          copyButton
          copyText={`companyID: ${companyID}`}
        />
      </div>

      {!isEnterpriseAccount && (
        <>
          <span className={styles.spacer} />
          <h3>Remove Account</h3>
          <div className={styles.subsection}>
            <p className={styles.byline}>
              Remove all user, account &amp; website data Uplifter has ever
              collected for your account. This action cannot be reversed.
            </p>
            <span className={styles.spacer} />
            <button
              className={styles.warningButton}
              onClick={() => {
                setActive(true)
              }}
            >
              Remove account
            </button>
          </div>
        </>
      )}

      {active && (
        <Modal
          active={active}
          narrow
          toggleActive={() => {
            setActive(false)
          }}
        >
          <ModalHead>
            <Heading type={3} align="left">
              Are you sure you wish to permanently delete your Uplifter account?
            </Heading>
            <p>This will remove all data and cannot be reversed.</p>
          </ModalHead>
          <ModalBody>
            <Label id="deleteAccount" modalHeading>
              Type in {deleteWord} to confirm account deletion
            </Label>
            <Input
              name="deleteAccount"
              type="text"
              value={inputValue}
              onValueChange={(e): void => {
                setInputValue(e)
              }}
              style={{ maxWidth: 600 }}
              required
            />
            <div className={styles.messageButtons}>
              <Button
                submit
                disabled={!(inputValue === deleteWord)}
                onClick={async () => {
                  if (inputValue === deleteWord) {
                    setActive(false)
                    await removeAllAccounts()
                    logout()
                  }
                }}
              >
                Yes
              </Button>
              <Button
                type="hollowBorder"
                onClick={(): void => {
                  setActive(false)
                }}
              >
                Go back
              </Button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  )
}
